import * as React from 'react';
import { Lock } from 'lucide-react';
import {
  PREVIEW_PHASE_GATE_MIN,
  formatPreviewPhaseGateMessage,
} from '@volley/crucible-ipc-types/preview-gate-constants';

/** Spinning arc used while `dev:start` is mid-flight. */
function SpinnerGlyph(): React.JSX.Element {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

/** TV-frame play glyph (rect = TV bezel) used in the idle / ready state. */
function PlayGlyph(): React.JSX.Element {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <polygon fill="currentColor" stroke="none" points="9,8 15,12 9,16" />
    </svg>
  );
}

/**
 * NOT mounted in production (#4053 — verified via a repo-wide grep: zero
 * non-test `<PreviewLaunchButton` JSX call sites across `packages/**\/*.tsx`).
 * What ships today is `WorkspaceHeader.tsx`'s Preview mode-pill tab, a
 * second, hand-rolled implementation built from `derivePreviewLaunchPillState()`
 * (`workspace/plan-build-handoff.ts`), which itself calls the pure helpers
 * this file exports (`isPreviewPhaseGated`, `previewPhaseGateTooltip`).
 *
 * This component is a helper/logic module + test fixture: `isPreviewPhaseGated`,
 * `previewPhaseGateTooltip`, and the `PREVIEW_PHASE_GATE_MIN`/`PREVIEW_PHASE_TOTAL`
 * re-exports are load-bearing (consumed by `plan-build-handoff.ts` and
 * `preview-phase-preflight.ts`); the `PreviewLaunchButton` function component
 * itself is exercised only by this file's own tests
 * (`__tests__/PreviewLaunchButton.test.tsx`, `__tests__/Workspace.preview-gating.test.tsx`)
 * pinning the phase-gate predicate and copy against a real render, not by
 * any live call site.
 *
 * Historical note, left for archaeology: this was originally "Step 1 of
 * the Preview restoration plan" (`docs/_archive/plan-preview-overlay.md`,
 * now `status: superseded`, `superseded_by: design-preview-select-to-chat-prd`).
 * That plan was superseded once already, before `WorkspaceHeader.tsx`'s icon
 * button (itself since removed in favour of the mode-pill tab) took over the
 * mounted role this doc comment used to claim for this component.
 *
 * Disabled when no `gameId` is loaded — preview without a game has no URL to
 * point at. `loading` reserved for the auto-start path where the
 * orchestrator is mid-flight; the button shows a spinner glyph and stays
 * disabled until dev:start resolves.
 *
 * Issue #466 — `phaseGate` disables the button when the workspace is still
 * pre-Phase 4 (no runnable game yet). Phase 1/2 have no framework code;
 * Phase 3 has a partial framework but the workspace dep graph isn't
 * reliably built. Phase 4 is the first point at which clicking Preview
 * produces something instead of the opaque "server exited before becoming
 * ready" error. From Phase 4 onwards the gate releases — power users may
 * peek at WIP feature work even before Phase 7 (Ready). The gate is
 * deliberately advisory: pass `phaseGate=undefined` (the default — e.g.
 * while `usePhaseProgress` is still loading) and the button behaves as if
 * the gate weren't there, so we don't flicker disabled-then-enabled at
 * mount.
 *
 * Constants live in `shared/preview-gate-constants.ts` so the main-process
 * IPC preflight (`preview-phase-preflight.ts`, #472) imports the same
 * literals — no more duplicated `as const` definitions to drift.
 */
export {
  PREVIEW_PHASE_GATE_MIN,
  PREVIEW_PHASE_TOTAL,
} from '@volley/crucible-ipc-types/preview-gate-constants';

export interface PreviewPhaseGate {
  /** Current phase from `usePhaseProgress` — 1..7. */
  readonly currentPhase: number;
  /** Human-readable label from `DerivedPhaseWire.label`. */
  readonly phaseLabel: string;
}

/**
 * Pure predicate for the phase gate. Pulled out of the component so the
 * gating logic can be regression-tested without a React render.
 *
 * Returns `true` when the button should be disabled by the phase gate
 * (currentPhase < 4). Returns `false` when `gate` is `undefined`
 * (`usePhaseProgress` still loading) so the button stays enabled at
 * mount — see #466 design notes for the no-flicker requirement.
 */
export function isPreviewPhaseGated(gate: PreviewPhaseGate | undefined): boolean {
  if (gate === undefined) return false;
  return gate.currentPhase < PREVIEW_PHASE_GATE_MIN;
}

/**
 * Compose the tooltip text when the phase gate is engaged. Pulled out for
 * the same reason as `isPreviewPhaseGated` — pure helper, regression
 * testable, single source of truth for the copy. Delegates to the shared
 * formatter so the main-process IPC refusal (#472) emits byte-identical
 * message text.
 */
export function previewPhaseGateTooltip(gate: PreviewPhaseGate): string {
  return formatPreviewPhaseGateMessage(gate.currentPhase, gate.phaseLabel);
}

/** Subset of DeployState values relevant to the Preview button indicator. */
type PreviewDeployState = 'idle' | 'starting' | 'running' | 'deploying' | 'deployed' | 'error';

export function PreviewLaunchButton(props: {
  gameId: string | undefined;
  loading?: boolean;
  label?: string;
  disabledReason?: string;
  forceDisabled?: boolean;
  onClick: () => void;
  /**
   * Issue #466. When the workspace is still pre-Phase 4 the button is
   * disabled and shows a "game not yet runnable" tooltip. Pass `undefined`
   * while the `usePhaseProgress` hook is still loading so the button
   * doesn't flicker disabled-then-enabled at mount.
   */
  phaseGate?: PreviewPhaseGate;
  /** Current deploy/preview server state — drives the status dot on the button. */
  deployState?: PreviewDeployState;
}): React.JSX.Element {
  const { gameId, loading, label, disabledReason, forceDisabled, onClick, phaseGate, deployState } =
    props;
  const gated = isPreviewPhaseGated(phaseGate);
  const disabled = !gameId || !!loading || gated || !!forceDisabled;
  const title = !gameId
    ? 'Preview is unavailable until a game is loaded'
    : disabledReason
      ? disabledReason
      : loading
        ? 'Starting the dev server…'
        : gated && phaseGate !== undefined
          ? previewPhaseGateTooltip(phaseGate)
          : 'Open the live preview (display + controller)';
  // Issue #607 (CTO James, alpha.79, dice-hangman repro 2026-05-14).
  // The original #466 phase-gate styling used a single `opacity-40` +
  // `cursor-not-allowed` treatment, which read as "slightly muted but
  // clickable" on the dark Foundry theme. James tried to click the
  // button thinking it was active. The phase-gated case now layers
  // three affordances on top of the existing disabled styling:
  //   1. Inline `Phase {N}+` label inside the button (universally
  //      readable; ties the disabled state to the underlying reason
  //      with no hover required).
  //   2. Lock icon prefix instead of the play glyph (lock = "by
  //      design, not broken"; widely understood pictogram).
  //   3. Stronger `opacity-30` so the button reads as decisively
  //      unavailable rather than just dim.
  // The other two disabled reasons (`loading`, missing `gameId`) keep
  // their existing affordances because they're transient — the user
  // hasn't been turned away on purpose, they've just hit a moment
  // when the button isn't ready.
  //
  // Accessibility: we also surface `aria-disabled` (mirrors the
  // boolean `disabled` for assistive tech that doesn't read both) and
  // expand `aria-label` to mention the phase requirement so screen
  // readers announce "Preview, currently unavailable, requires phase
  // 4 or higher" instead of just "Preview, dimmed".
  //
  // The phase-gated affordance only paints when (a) the gate is
  // engaged, (b) the gate state has actually resolved (no flicker),
  // and (c) we're not in the loading-precedes-gate edge case (loading
  // implies the user has already clicked; the loading affordance
  // wins). Mirrors the `title` precedence chain —
  // `!gameId > loading > phase-gate > default` — so the visual and
  // the tooltip never disagree.
  const phaseGated = gated && phaseGate !== undefined && !loading && !!gameId;
  const ariaLabel = phaseGated
    ? `Open preview (unavailable until phase ${PREVIEW_PHASE_GATE_MIN})`
    : 'Open preview';
  const className = phaseGated
    ? 'flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 ' +
      'px-2.5 py-1 text-xs font-medium text-white/70 transition-colors ' +
      'disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-white/10 ' +
      'disabled:hover:bg-white/5 disabled:hover:text-white/70'
    : 'flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 ' +
      'px-2.5 py-1 text-xs font-medium text-white/70 transition-colors ' +
      'hover:border-crucible-accent/40 hover:bg-crucible-accent/10 hover:text-white ' +
      'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/10 ' +
      'disabled:hover:bg-white/5 disabled:hover:text-white/70';

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled ? 'true' : 'false'}
      data-testid="preview-launch-button"
      data-phase-gated={gated ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
      title={title}
      aria-label={ariaLabel}
      className={className}
    >
      {phaseGated ? (
        <span data-testid="preview-launch-button-lock-glyph">
          <Lock size={13} strokeWidth={1.75} aria-hidden="true" />
        </span>
      ) : loading ? (
        <SpinnerGlyph />
      ) : (
        <PlayGlyph />
      )}
      <span>{label ?? 'Preview'}</span>
      {phaseGated ? (
        <span
          data-testid="preview-launch-button-phase-hint"
          className="ml-0.5 rounded-sm border border-white/10 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-white/60"
        >
          Phase {PREVIEW_PHASE_GATE_MIN}+
        </span>
      ) : null}
      {!phaseGated && deployState && deployState !== 'idle' && <StatusDot state={deployState} />}
    </button>
  );
}

/**
 * Small status dot that appears inside the Preview button when the
 * preview server / deploy pipeline is in a notable state. Sits after
 * the "Preview" label so it doesn't disrupt reading order.
 *
 * running  → green pulse  (server is live)
 * starting → amber pulse  (server coming up — mirrors the spinner but adds colour)
 * deploying → amber pulse (pushing to TV)
 * deployed → green solid  (on TV)
 * error    → red solid    (something broke)
 */
function StatusDot({ state }: { state: PreviewDeployState }): React.JSX.Element {
  const base = 'h-1.5 w-1.5 shrink-0 rounded-full';
  if (state === 'running' || state === 'deployed') {
    return <span className={`${base} bg-green-400`} aria-hidden="true" />;
  }
  if (state === 'deploying' || state === 'starting') {
    return <span className={`${base} bg-amber-400 animate-pulse`} aria-hidden="true" />;
  }
  if (state === 'error') {
    return <span className={`${base} bg-red-400`} aria-hidden="true" />;
  }
  return <></>;
}
